import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useMembershipExtensionListingAPI } from "@/app/hooks/api_hooks/MembershipExtension/useMembershipExtensionListingAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IMembershipExtensionState } from "@/app_redux/reducers/slices/auth/membership_extension.slice";
import { PRIMARY_BUTTON } from "@/constants";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { TbCircleLetterM } from "react-icons/tb";
import { useNavigate } from "react-router";
import { IMembershipExtensionListingResponseData } from "@/api_framework/api_modals/MembershipExtension";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  MEMBERSHIP_EXTENSION_CLICK_EVENTS,
  MEMBERSHIP_EXTENSION_SCREEN_IDS,
  MembershipExtensionClickEvent,
  buildMembershipExtensionClickLog,
  getMembershipExtensionId,
  isMembershipExtensionActive,
} from "./utils";

const tableHeader = [
  { item: "Extension Name" },
  { item: "Category" },
  { item: "Amount" },
  { item: "Extension Days" },
  { item: "Created by" },
  { item: "Status" },
  { item: "Action" },
];

const MembershipExtensionSetup = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("MEMBERSHIP_EXTENSION", "id");
  const { routeState } = useRouteInfo(routeId);
  const { NO_APPOINTMENTS } = useImageConstants();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const isMembershipExtensionListInProgess = useLoader("isMembershipExtensionListInProgress");
  const { membershipExtensionList } = routeState.state as IMembershipExtensionState;
  const { execute: fetchMembershipExtensionList } = useMembershipExtensionListingAPI();
  const { execute: logClickEvent } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const logExtensionEvent = (eventType: MembershipExtensionClickEvent, extra: Partial<IClickLogBody> = {}) => {
    logClickEvent(
      buildMembershipExtensionClickLog(
        eventType,
        MEMBERSHIP_EXTENSION_SCREEN_IDS.LIST,
        { clinic_id: mappedClinicId, clinic_name: mappedClinicName, vms_user_id: vmsUserId },
        extra,
      ),
    );
  };

  useEffect(() => {
    fetchMembershipExtensionList(searchQuery);
  }, [searchQuery]);

  const handelExtensionSearch = (query: string) => {
    logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.EXTENSION_SEARCH_CLICK, { search_item: query?.trim() });
    setSearchQuery(query);
  };

  const handelExtensionEdit = (extension: IMembershipExtensionListingResponseData) => {
    const extension_id = getMembershipExtensionId(extension);
    logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.EXTENSION_EDIT_CLICK, {
      extension_id,
      extension_name: extension?.extension_name,
      extension_category: extension?.category,
      extension_amount: extension?.amount,
      extension_days: extension?.extension_days,
      extension_status: `${extension?.status}`,
    });
    navigate(`${getRouteKey("MEMBERSHIP_EXTENSION_CREATION", "url")}?extension_id=${extension_id}`);
  };

  const handelExtensionCreation = () => {
    logExtensionEvent(MEMBERSHIP_EXTENSION_CLICK_EVENTS.EXTENSION_SETUP_CLICK, {
      cta_text: "Membership Extension Setup",
    });
    navigate(getRouteKey("MEMBERSHIP_EXTENSION_CREATION", "url"));
  };

  return (
    <div>
      <div className="px-4 sm:px-6 lg:px-8 mt-2 pt-3 bg-white">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto mb-3">
            <h1 className="text-base font-medium leading-6 text-black">Membership Extensions</h1>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="w-1/4">
            <InputWithAddOn
              onEnterKeyPress={(query: string) => handelExtensionSearch(query)}
              containerClassName="w-full"
              addon={<TbCircleLetterM className="w-4 h-4" />}
              placeholder="Search by Extension Name"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <button onClick={handelExtensionCreation} className={`${PRIMARY_BUTTON} cursor-pointer`}>
            Membership Extension Setup
          </button>
        </div>
      </div>
      <div className="px-4 sm:px-6 lg:px-8 pb-4 bg-white">
        <div className="mt-6 flow-root">
          {isMembershipExtensionListInProgess ? (
            <TableSkeleton />
          ) : membershipExtensionList?.length === 0 ? (
            <NoDataFoundComponent
              imageURL={NO_APPOINTMENTS}
              message="No Membership Extension Available"
              description="No membership extensions have been available, please onboard some membership extension through create membership extension."
            />
          ) : (
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader?.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {key.item}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {membershipExtensionList?.map((extension, id) => {
                            const isFirst = id === 0;
                            const isLast = id === membershipExtensionList?.length - 1;
                            const isSingle = membershipExtensionList?.length === 1;
                            const isActive = isMembershipExtensionActive(extension?.status);

                            return (
                              <tr
                                key={id}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 text-xs font-normal max-w-[200px]",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  <span className="block whitespace-normal break-words">
                                    {extension?.extension_name}
                                  </span>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                  {extension?.category}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                  {extension?.amount}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                  {extension?.extension_days}
                                </td>
                                <td className="px-3 py-3 text-xs font-normal text-[#6C727F]">
                                  {extension?.created_by}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                  <div
                                    className={clsx(
                                      "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium",
                                      isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700",
                                    )}
                                  >
                                    <span
                                      className={clsx("w-2 h-2 rounded-full", isActive ? "bg-green-500" : "bg-red-500")}
                                    />
                                    {isActive ? "Active" : "Inactive"}
                                  </div>
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                >
                                  <a
                                    className="text-[#1F1F23] hover:bg-slate-200 bg-slate-100 inline-flex h-7 w-7 items-center justify-center rounded-xl transition cursor-pointer"
                                    onClick={() => handelExtensionEdit(extension)}
                                  >
                                    <PencilSquareIcon className="w-4 h-4" />
                                  </a>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipExtensionSetup;
